/* global $ */
import { useEffect, useRef } from 'preact/hooks';
import getCalendarRanges from '../../../utils/getCalendarRanges';
// jQuery plugins - loaded via jQueryPluginsLoader Vite plugin (see vite.config.js)
import 'pickadate/lib/compressed/picker';
import 'pickadate/lib/compressed/picker.date';
import getProductType from '../../../utils/getProductType';
import { 
  getVisibleMonth,
  addEventEmojisToCalendar,
} from '../utils/pickerHelpers';

/**
 * Hook to initialize the pickadate calendar instance
 * Handles only the initialization logic, returns control functions
 * 
 * @param {Object} params
 * @param {Object} params.inputRef - Ref to the input element
 * @param {Object} params.fieldWrapperRef - Ref to the field wrapper
 * @param {Object} params.config - Calendar configuration
 * @param {string} params.config.field - Field name (departureDate/returnDate)
 * @param {number} params.config.maxDaysSearch - Maximum days for search
 * @param {string} params.config.firstAvailableDate - First selectable date (YYYY-MM-DD), blocks the days before it
 * @param {string} params.config.dateFormat - Date format
 * @param {boolean} params.config.hide - Hide input flag
 * @param {boolean} params.config.skipInitialValidation - Skip initial validation
 * @param {string} params.config.way - Way type (departure/return)
 * @param {Object} params.callbacks - Lifecycle callbacks
 * @param {Function} params.callbacks.onOpen - Called when calendar opens
 * @param {Function} params.callbacks.onClose - Called when calendar closes
 * @param {Function} params.callbacks.onSet - Called when date is set
 * @param {Object} params.actions - Control actions (passed from parent)
 * @param {Function} params.actions.setDate - Function to set date
 * @param {Function} params.actions.setOpenTicketBtn - Function to set open ticket button
 * @param {Function} params.actions.setDatePickerText - Function to set date picker text
 * @param {Object} params.eventHandlers - Custom event handlers
 * @param {Function} params.eventHandlers.onChangeDepartureDate - Handler for departure date change
 * @param {Function} params.eventHandlers.onChangeReturnDate - Handler for return date change
 * @param {Function} params.eventHandlers.onClean - Handler for clean event
 * @param {Object} params.options - Additional options
 * @param {Function} params.options.setDeparturePicker - Set departure picker reference
 * @param {Function} params.options.onDatePickerCreated - Callback when picker is created
 * @param {Array} params.events - Calendar events to display on days (optional)
 * @returns {Object} Control functions { setDate, setOpenTicketBtn, setDatePickerText }
 */
const useCalendarInit = ({
  inputRef,
  fieldWrapperRef,
  config,
  callbacks,
  actions,
  eventHandlers,
  options = {},
  events = [],
}) => {
  const {
    field,
    maxDaysSearch,
    firstAvailableDate,
    dateFormat,
    hide = false,
    skipInitialValidation = false,
    way,
    pickerLocale,
  } = config;

  const {
    onOpen,
    onClose,
    onSet,
  } = callbacks;

  const {
    onChangeDepartureDate,
    onChangeReturnDate,
    onClean,
  } = eventHandlers;

  const {
    setDeparturePicker,
    onDatePickerCreated,
  } = options;

  // Ref to always have access to latest events in callbacks
  // This ensures onRender callback always uses the most recent events
  const eventsRef = useRef(events);
  
  // Update ref whenever events change
  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  useEffect(() => {
    if (!fieldWrapperRef.current) return;

    // Initialize the picker input reference
    inputRef.current = $(fieldWrapperRef.current).find(`input.${field}`);
    const $input = inputRef.current;

    // Calculate min/max dates
    let minMax = {};
    if (!skipInitialValidation) {
      const { min, max } = getCalendarRanges(maxDaysSearch, firstAvailableDate);
      minMax = { min, max };
    }

    // Initialize pickadate
    $input.pickadate({
      ...(pickerLocale || {}),
      ...minMax,
      ...(field !== 'returnDate' && { clear: false }),
      onClose: () => {
        if (onClose) onClose({ value: $input.val() });
      },
      onOpen: () => {
        // Close the other datepicker if it's open
        // This ensures only one calendar is open at a time
        const otherField = field === 'departureDate' ? 'returnDate' : 'departureDate';
        try {
          const $otherInput = $(`input.${otherField}`);
          if ($otherInput.length) {
            const otherPicker = $otherInput.pickadate('picker');
            if (otherPicker && otherPicker.get('open')) {
              otherPicker.close();
            }
          }
        } catch (err) {
          // Silently fail if other picker is not initialized
        }
        
        // Call parent callback
        if (onOpen) onOpen({ field });
      },
      onSet: () => {
        if (onSet) onSet({ field, value: $input.val() });
      },
      onRender: () => {
        // Use eventsRef to always get the latest events, not the closure value
        const currentEvents = eventsRef.current;
        // onRender fires when calendar is rendered, including when month changes
        // This allows us to detect month changes and notify parent component
        if (callbacks.onMonthChange) {
          const visibleMonth = getVisibleMonth($input);
          if (visibleMonth) {
            callbacks.onMonthChange({ field, visibleMonth });
          }
        }
        
        // Add event emojis to calendar days
        // Use eventsRef to always get the latest events, not the closure value
        if (currentEvents && currentEvents.length > 0) {
          setTimeout(() => {
            addEventEmojisToCalendar({ $input, events: currentEvents });
          }, 0);
        }
      },
      ...(dateFormat && { format: dateFormat }),
    });

    // Bind custom events
    $input.on('changeDepartureDate', onChangeDepartureDate);
    $input.on('changeReturnDate', onChangeReturnDate);
    $input.on('clearDate', onClean);

    // Set departure picker reference
    if (way === 'departure' && setDeparturePicker) {
      setDeparturePicker($input);
    }

    // Hide input if needed
    if (hide) {
      $input.hide();
    }

    // Notify parent that picker is ready
    if (onDatePickerCreated) onDatePickerCreated(way);

    // Moving the calendar to the portal (mobile only)
    const isMobile = getProductType() === 'web-mobile';
    if (isMobile) {
      $('.picker').appendTo($('#reservamosPortal'));
    }

    // Cleanup function
    return () => {
      if ($input && $input.pickadate) {
        $input.off('changeDepartureDate');
        $input.off('changeReturnDate');
        $input.off('clearDate');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  // Setup initial state after initialization (runs once on mount)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!inputRef.current) return;

      // Set initial date if provided
      if (config.date) {
        actions.setDate(config.date);
      }

      // Set open ticket button
      actions.setOpenTicketBtn(field, config.calendarOpen);

      // Set date picker text
      actions.setDatePickerText(field);
    }, 0);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once after mount

  // Return control functions (these are wrappers that use the actions passed from parent)
  return {
    setDate: actions.setDate,
    setOpenTicketBtn: actions.setOpenTicketBtn,
    setDatePickerText: actions.setDatePickerText,
  };
};

export default useCalendarInit;

